import { useEffect, useMemo, useState } from 'react';
import { LoginAsCamp, money, useDebounced, useLoad } from '../lib';
import {
  records, type ApplyResult, type Body, type CampRecord, type CampUser, type CampVendor, type Category, type PermissionTemplate, type RecordOptions,
} from '../records-api';
import {
  CategoryPicker, ChangesCard, ChipListEditor, FieldGrid, OutcomeLine, SaveBar, SystemOfRecordNote, VendorPicker, WeekdayPicker,
  LIMIT_LABEL, PACKAGE_LABEL, dayNames, errText, parseDays, useRecordForm, type FieldDef, type Outcome,
} from '../records';

// Camps — the camp (Adobe "customer"/company) master record: profile, price-list group, GL codes,
// category blocks, sub-accounts and their approvers, assigned vendors and their delivery settings, and
// block/unblock (parity review 2026-10-16, Tier 1 #3). Reads for all staff; edits for admin/ops.

const STATUS_LABEL: Record<number, string> = { 0: 'pending', 1: 'active', 2: 'rejected', 3: 'blocked' };
const STATUS_FILTERS = [['active', 'Active'], ['blocked', 'Blocked'], ['all', 'All']] as const;

export function CampsPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>('active');
  const dq = useDebounced(q);
  const [open, setOpen] = useState<number | null>(null);
  const { data, err } = useLoad(() => records.camps(dq, status), [dq, status]);

  if (open != null) return <CampDetail id={open} canEdit={canEdit} onBack={() => setOpen(null)} />;

  return (
    <div className="stack">
      <h1>Camps</h1>
      <p className="sub">Each camp's profile, price list, GL codes, category blocks, people and vendors — open one to edit it</p>
      <div className="toolbar">
        <input className="search" placeholder="Search by camp, id or H&H account…" value={q} onChange={(e) => setQ(e.target.value)} />
        {STATUS_FILTERS.map(([v, l]) => (
          <button key={v} className={`chip ${status === v ? 'on' : ''}`} onClick={() => setStatus(v)}>{l}</button>
        ))}
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card mr-scroll">
        <table>
          <thead><tr><th>Camp</th><th>Price list</th><th>Type</th><th>Location</th><th>H&H acct</th><th className="num">Vendors</th><th className="num">People</th><th>Status</th></tr></thead>
          <tbody>
            {(data ?? []).map((c) => (
              <tr key={c.id} className="row-click" onClick={() => setOpen(c.id)}>
                <td><strong>{c.name}</strong> <span className="mono">#{c.id}</span></td>
                <td className="mono">{c.cohort ?? '—'}</td>
                <td>{c.camp_type ?? '—'}</td>
                <td>{c.physical_location ?? '—'}</td>
                <td className="mono">{c.account_id ?? '—'}</td>
                <td className="num">{c.vendors}</td>
                <td className="num">{c.users}</td>
                <td><StatusPill status={c.status} /></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={8} className="loading">No camps match.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}

function StatusPill({ status }: { status: number }) {
  const s = STATUS_LABEL[status] ?? String(status);
  return <span className={`pill ${s === 'active' ? 'edi' : s === 'blocked' ? 'mr-blocked' : ''}`}>{s}</span>;
}

/** The camp's own fields and its profile, as one form. */
function accountFields(o: RecordOptions): FieldDef[] {
  return [
    { key: 'name', col: 'name', label: 'Camp name', kind: 'text' },
    { key: 'legalName', col: 'legal_name', label: 'Legal name', kind: 'text' },
    { key: 'cohortId', col: 'cohort_id', label: 'Price-list group', kind: 'select', options: o.cohorts.map((c) => ({ value: String(c.id), label: c.code })) },
    { key: 'accountId', col: 'account_id', label: 'H&H account', kind: 'text' },
    { key: 'email', col: 'email', label: 'Company email', kind: 'email' },
    { key: 'telephone', col: 'telephone', label: 'Telephone', kind: 'text' },
    { key: 'apEmail', col: 'ap_email', label: 'AP email', kind: 'email' },
    { key: 'billPayEmail', col: 'bill_pay_email', label: 'Bill-pay invoice email', kind: 'email' },
    { key: 'primaryContact', col: 'primary_contact', label: 'Primary contact', kind: 'text' },
    { key: 'primaryContactRole', col: 'primary_contact_role', label: 'Contact title / dept', kind: 'text' },
    { key: 'campType', col: 'camp_type', label: 'Camp type', kind: 'select', options: o.campTypes },
    { key: 'foodType', col: 'food_type', label: 'Food type', kind: 'select', options: o.foodTypes },
    { key: 'physicalLocation', col: 'physical_location', label: 'Physical location', kind: 'select', options: o.locations },
    { key: 'yearRound', col: 'year_round', label: 'Year round', kind: 'bool' },
    { key: 'sumDropboxUrl', col: 'sum_dropbox_url', label: 'SUM report Dropbox URL', kind: 'url' },
    { key: 'dropboxPath', col: 'dropbox_path', label: 'Dropbox path', kind: 'text' },
    { key: 'taxExempt', col: 'tax_exempt', label: 'Tax exempt', kind: 'bool' },
    { key: 'invoiceReview', col: 'invoice_review', label: 'Invoice review', kind: 'bool' },
    { key: 'autoLockPackages', col: 'auto_lock_packages', label: 'Auto-lock packages', kind: 'bool' },
    { key: 'showCheckoutAlert', col: 'show_checkout_alert', label: 'Checkout alert', kind: 'bool' },
    { key: 'canCreateSublogins', col: 'can_create_sublogins', label: 'Can create sub-logins', kind: 'bool' },
    { key: 'maxSublogins', col: 'max_sublogins', label: 'Max sub-logins', kind: 'number' },
    { key: 'followGroupProducts', col: 'follow_group_products', label: 'Follow group products', kind: 'bool' },
    { key: 'notes', col: 'notes', label: 'Notes', kind: 'textarea' },
  ];
}

function CampDetail({ id, canEdit, onBack }: { id: number; canEdit: boolean; onBack: () => void }) {
  const [d, setD] = useState<CampRecord | null>(null);
  const [opts, setOpts] = useState<RecordOptions | null>(null);
  const [cats, setCats] = useState<Category[]>([]);
  const [templates, setTemplates] = useState<PermissionTemplate[]>([]);
  const [err, setErr] = useState('');
  const [out, setOut] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    records.camp(id).then(setD).catch((e) => setErr(errText(e)));
    records.templates().then((r) => setTemplates(r.templates.filter((t) => t.is_active))).catch(() => setTemplates([]));
    records.options().then(setOpts).catch((e) => setErr(errText(e)));
    records.categories().then(setCats).catch(() => setCats([]));
  }, [id]);

  /**
   * Run one save and answer whether it was accepted; its outcome shows beside the control that made
   * it (`tag`). On success the record the answer carries replaces the one on screen.
   */
  async function save(tag: string, run: () => Promise<{ changed?: string[] } & Partial<CampRecord>>, what: string): Promise<boolean> {
    setBusy(tag); setOut(null);
    try {
      const r = await run();
      setD(r.camp ? (r as CampRecord) : await records.camp(id));
      setOut({ tag, bad: false, text: r.changed && r.changed.length === 0 ? 'Nothing changed.' : `Saved ${what}.` });
      return true;
    } catch (e) {
      setOut({ tag, bad: true, text: errText(e) });
      return false;
    } finally { setBusy(''); }
  }
  const outFor = (prefix: string) => (out && (out.tag === prefix || out.tag.startsWith(`${prefix}-`)) ? out : null);

  /** Apply a template to some of the camp's people; the answer says, per person, what it changed. */
  async function applyTemplate(t: PermissionTemplate, userIds: number[]): Promise<boolean> {
    setBusy('apply'); setOut(null);
    try {
      const r: ApplyResult = await records.applyTemplate(id, t.id, userIds);
      setD(r);
      const fields = r.applied.reduce((n, a) => n + a.changed.length, 0);
      const people = `${r.applied.length} ${r.applied.length === 1 ? 'person' : 'people'}`;
      setOut({ tag: 'apply', bad: false, text: `Applied "${t.name}" to ${people} — ${fields === 0 ? 'they already matched it' : `${fields} field${fields === 1 ? '' : 's'} changed`}. Make the same change in Adobe.` });
      return true;
    } catch (e) {
      setOut({ tag: 'apply', bad: true, text: errText(e) });
      return false;
    } finally { setBusy(''); }
  }

  if (!d || !opts) {
    return <div className="stack"><div><button className="btn-sm ghost" onClick={onBack}>← Back to camps</button></div>{err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading…</p>}</div>;
  }
  const c = d.camp;
  const blocked = c.status === 3;

  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={onBack}>← Back to camps</button></div>
      <div className="mr-head">
        <h1>{c.name}</h1>
        <StatusPill status={c.status} />
        <LoginAsCamp orgId={c.id} name={c.name} />
        {canEdit && (c.status === 1 || c.status === 3) && (
          <button className={`btn-sm ${blocked ? '' : 'ghost'}`} disabled={busy !== ''}
            onClick={() => {
              if (!blocked && !window.confirm(`Block ${c.name}? Its people are refused on their next request until it is unblocked.`)) return;
              save('status', () => records.saveCamp(id, { status: blocked ? 'active' : 'blocked' }), blocked ? 'the camp as active' : 'the block');
            }}>{blocked ? 'Unblock camp' : 'Block camp'}</button>
        )}
      </div>
      <p className="sub">Camp {c.id} · price list {c.cohort ?? '—'}{c.city ? ` · ${c.city}` : ''}{c.super_user_id ? ` · master account ${c.super_user_id}` : ''}{c.is_demo ? ' · demo' : ''}</p>
      <SystemOfRecordNote />
      {err && <p className="msg bad">⚠ {err}</p>}
      {outFor('status') && <p className={`msg ${outFor('status')!.bad ? 'bad' : 'good'}`}>{outFor('status')!.text}</p>}

      <AccountCard d={d} opts={opts} canEdit={canEdit} busy={busy} outcome={outFor('account')} onSave={(body) => save('account', () => records.saveCamp(id, body), 'the account')} />
      <GlCodesCard d={d} canEdit={canEdit} busy={busy} outcome={outFor('gl')} onSave={(codes) => save('gl', () => records.saveGlCodes(id, codes), 'the GL codes')} />
      <section className="card">
        <h2>Blocked categories</h2>
        <p className="muted mr-lead">Categories hidden from the whole camp (Adobe's "Block Selected Categories" on the master account). A person can have their own blocks too — see People.</p>
        <BlocksEditor key={JSON.stringify(d.profile?.blocked_category_ids ?? [])} ids={d.profile?.blocked_category_ids ?? []} cats={cats} canEdit={canEdit}
          busy={busy === 'blocks'} outcome={outFor('blocks')} onSave={(ids) => save('blocks', () => records.saveCampBlocks(id, ids), 'the category blocks')} />
      </section>
      <PeopleCard d={d} opts={opts} cats={cats} templates={templates} canEdit={canEdit} busy={busy} outcome={outFor('user')}
        onSave={(userId, body) => save(`user-${userId}`, () => records.saveCampUser(id, userId, body), 'the person')}
        applyOutcome={outFor('apply')} onApply={(t, userIds) => applyTemplate(t, userIds)} />
      <VendorsCard d={d} canEdit={canEdit} busy={busy} outcome={out && (out.tag === 'assign' || out.tag.startsWith('vendor-')) ? out : null}
        onAssign={(vendorId) => save('assign', () => records.assignVendor(id, vendorId), 'the vendor assignment')}
        onSave={(vendorId, body) => save(`vendor-${vendorId}`, () => records.saveCampVendor(id, vendorId, body), 'the vendor settings')} />
      <ChangesCard changes={d.changes} />
    </div>
  );
}

function AccountCard({ d, opts, canEdit, busy, outcome, onSave }: {
  d: CampRecord; opts: RecordOptions; canEdit: boolean; busy: string; outcome: Outcome | null; onSave: (body: Body) => void;
}) {
  const defs = useMemo(() => accountFields(opts), [opts]);
  const rec = useMemo(() => ({ ...d.camp, ...(d.profile ?? {}) }), [d]);
  const f = useRecordForm(defs, rec as unknown as Record<string, unknown>);
  return (
    <section className="card">
      <h2>Account</h2>
      <FieldGrid defs={defs} form={f.form} onChange={f.setForm} disabled={!canEdit} />
      {canEdit && <SaveBar dirty={f.dirty} busy={busy === 'account'} onSave={() => onSave(f.changes)} onReset={f.reset} outcome={outcome}
        hint="The price-list group decides which catalog the camp sees in Adobe." />}
    </section>
  );
}

function GlCodesCard({ d, canEdit, busy, outcome, onSave }: {
  d: CampRecord; canEdit: boolean; busy: string; outcome: Outcome | null; onSave: (codes: string[]) => void;
}) {
  const initial = d.profile?.gl_codes ?? [];
  const [codes, setCodes] = useState(initial);
  useEffect(() => setCodes(d.profile?.gl_codes ?? []), [d]);
  const dirty = JSON.stringify(codes) !== JSON.stringify(initial);
  return (
    <section className="card">
      <h2>GL codes</h2>
      <p className="muted mr-lead">The codes a buyer picks from when placing an order (Adobe's "GL Codes" tab).</p>
      <ChipListEditor items={codes} onChange={setCodes} disabled={!canEdit} placeholder="Add a GL code…" />
      {canEdit && <SaveBar dirty={dirty} busy={busy === 'gl'} onSave={() => onSave(codes)} onReset={() => setCodes(initial)} outcome={outcome} />}
    </section>
  );
}

function BlocksEditor({ ids, cats, canEdit, busy, outcome, onSave }: {
  ids: number[]; cats: Category[]; canEdit: boolean; busy: boolean; outcome: Outcome | null; onSave: (ids: number[]) => void;
}) {
  const [sel, setSel] = useState(ids);
  const dirty = JSON.stringify(sel) !== JSON.stringify(ids);
  return (
    <>
      <CategoryPicker categories={cats} ids={sel} onChange={setSel} disabled={!canEdit} />
      {canEdit && <SaveBar dirty={dirty} busy={busy} onSave={() => onSave(sel)} onReset={() => setSel(ids)} outcome={outcome} />}
    </>
  );
}

const roleOf = (u: CampUser) => (u.master ? 'administrator'
  : u.place_order ? 'orders' : u.order_approval_needed ? 'orders, needs approval' : u.place_order === false ? 'cannot order' : '—');

function PeopleCard({ d, opts, cats, templates, canEdit, busy, outcome, onSave, applyOutcome, onApply }: {
  d: CampRecord; opts: RecordOptions; cats: Category[]; templates: PermissionTemplate[]; canEdit: boolean; busy: string; outcome: Outcome | null;
  onSave: (userId: number, body: Body) => Promise<boolean>;
  applyOutcome: Outcome | null; onApply: (t: PermissionTemplate, userIds: number[]) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [templateId, setTemplateId] = useState('');
  const names = new Map(d.users.map((u) => [u.id, u.name ?? u.email ?? `#${u.id}`]));
  // The master account holds every permission (its Adobe role is the administrator's): a template is for sub-accounts.
  const subs = d.users.filter((u) => !u.master);
  const cols = canEdit ? 8 : 7;
  const tpl = templates.find((t) => String(t.id) === templateId);
  const apply = async () => {
    if (!tpl || picked.length === 0) return;
    const who = picked.length === 1 ? names.get(picked[0]) : `${picked.length} people`;
    if (!window.confirm(`Apply "${tpl.name}" to ${who}? Their ordering permissions, order limit, package email and blocked categories are replaced with the template's.`)) return;
    if (await onApply(tpl, picked)) setPicked([]);
  };
  return (
    <section className="card">
      <h2>People ({d.users.length})</h2>
      <p className="muted mr-lead">The camp's master account and sub-accounts: job title, who approves their orders, order limits and package email. "Ordering" is what their Adobe role allows at checkout.</p>
      {canEdit && subs.length > 0 && (
        <div className="pt-apply">
          <select value={templateId} aria-label="Permission template" disabled={busy !== ''} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Apply a permission template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn-sm" disabled={!tpl || picked.length === 0 || busy !== '' || editing != null} onClick={apply}>
            {busy === 'apply' ? 'Applying…' : `Apply to ${picked.length || 'selected'}${picked.length ? (picked.length === 1 ? ' person' : ' people') : ''}`}
          </button>
          {applyOutcome ? <OutcomeLine o={applyOutcome} /> : <span className="muted small">Tick the people to apply it to.</span>}
        </div>
      )}
      {editing == null && <p className="mr-lead"><OutcomeLine o={outcome} /></p>}
      <div className="mr-scroll">
        <table>
          <thead><tr>
            {canEdit && (
              <th className="chk-col">
                <input type="checkbox" aria-label="Select every sub-account" disabled={subs.length === 0}
                  checked={subs.length > 0 && picked.length === subs.length}
                  onChange={(e) => setPicked(e.target.checked ? subs.map((u) => u.id) : [])} />
              </th>
            )}
            <th>Person</th><th>Title</th><th>Ordering</th><th>Approvals</th><th>Order limit</th><th>Package email</th><th></th>
          </tr></thead>
          <tbody>
            {d.users.map((u) => (
              editing === u.id ? (
                <tr key={u.id}><td colSpan={cols}>
                  <UserEditor u={u} users={d.users} opts={opts} cats={cats} busy={busy === `user-${u.id}`} outcome={outcome}
                    onCancel={() => setEditing(null)} onSave={async (body) => { if (await onSave(u.id, body)) setEditing(null); }} />
                </td></tr>
              ) : (
                <tr key={u.id} className={u.is_active === false ? 'inactive' : undefined}>
                  {canEdit && (
                    <td className="chk-col">
                      {!u.master && (
                        <input type="checkbox" aria-label={`Select ${names.get(u.id)}`} checked={picked.includes(u.id)}
                          onChange={(e) => setPicked(e.target.checked ? [...picked, u.id] : picked.filter((x) => x !== u.id))} />
                      )}
                    </td>
                  )}
                  <td>
                    <div>{u.name ?? '—'}{u.master && <span className="you">master</span>}</div>
                    <div className="mono">{u.email ?? `#${u.id}`}</div>
                    {u.template && <div className="muted small">Template: {u.template.replace(/^\d+ /, '')}</div>}
                    {u.blocked_category_ids.length > 0 && <div className="muted small">{u.blocked_category_ids.length} blocked categories</div>}
                  </td>
                  <td>{u.title ?? '—'}</td>
                  <td className="small">{roleOf(u)}</td>
                  <td className="small">
                    {u.is_approver || u.approve_orders ? <div>Approves carts{u.is_approver ? '' : ' (by role)'}</div> : null}
                    {u.approver_ids.length > 0 && <div>Approved by {u.approver_ids.map((x) => names.get(x) ?? `#${x}`).join(', ')}</div>}
                    {!u.is_approver && !u.approve_orders && u.approver_ids.length === 0 && '—'}
                  </td>
                  <td>{u.order_limit_period ? `${money(u.order_limit_value)} ${LIMIT_LABEL[u.order_limit_period] ?? ''}` : '—'}</td>
                  <td className="small">{u.package_email ? PACKAGE_LABEL[u.package_email] ?? u.package_email : '—'}</td>
                  <td>{canEdit && <button className="btn-sm ghost" disabled={busy !== '' || editing != null} onClick={() => setEditing(u.id)}>Edit</button>}</td>
                </tr>
              )
            ))}
            {d.users.length === 0 && <tr><td colSpan={cols} className="loading">No accounts at this camp.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UserEditor({ u, users, opts, cats, busy, outcome, onSave, onCancel }: {
  u: CampUser; users: CampUser[]; opts: RecordOptions; cats: Category[]; busy: boolean; outcome: Outcome | null;
  onSave: (body: Body) => void; onCancel: () => void;
}) {
  const [title, setTitle] = useState(u.title ?? '');
  const [isApprover, setIsApprover] = useState(u.is_approver === true);
  const [period, setPeriod] = useState(u.order_limit_period ?? '');
  const [limit, setLimit] = useState(u.order_limit_value == null ? '' : String(u.order_limit_value));
  const [approvers, setApprovers] = useState<number[]>(u.approver_ids);
  const [pkg, setPkg] = useState(u.package_email ?? '');
  const [blocks, setBlocks] = useState<number[]>(u.blocked_category_ids);
  const body: Body = {};
  if (title !== (u.title ?? '')) body.title = title || null;
  if (isApprover !== (u.is_approver === true)) body.isApprover = isApprover;
  if (period !== (u.order_limit_period ?? '')) body.orderLimitPeriod = period || null;
  if (limit !== (u.order_limit_value == null ? '' : String(u.order_limit_value))) body.orderLimitValue = limit.trim() || null;
  if (JSON.stringify(approvers) !== JSON.stringify(u.approver_ids)) body.approverIds = approvers;
  if (pkg !== (u.package_email ?? '')) body.packageEmail = pkg || null;
  if (JSON.stringify(blocks) !== JSON.stringify(u.blocked_category_ids)) body.categoryIds = blocks;
  const others = users.filter((x) => x.id !== u.id);
  return (
    <div className="stack mr-tight mr-edit">
      <strong>{u.name ?? u.email ?? `#${u.id}`}</strong>
      <div className="tf-grid">
        <label>Job title
          <select value={title} onChange={(e) => setTitle(e.target.value)}><option value="">—</option>{opts.jobTitles.map((t) => <option key={t}>{t}</option>)}</select>
        </label>
        <label>Order limit
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="">Not limited</option>{opts.limitPeriods.map((p) => <option key={p} value={p}>{LIMIT_LABEL[p] ?? p}</option>)}
          </select>
        </label>
        <label>Limit amount ($)
          <input inputMode="decimal" value={limit} disabled={!period} onChange={(e) => setLimit(e.target.value)} placeholder="—" />
        </label>
        <label>Package email
          <select value={pkg} onChange={(e) => setPkg(e.target.value)}><option value="">—</option>{opts.packageEmail.map((p) => <option key={p} value={p}>{PACKAGE_LABEL[p] ?? p}</option>)}</select>
        </label>
        <label className="chk"><input type="checkbox" checked={isApprover} onChange={(e) => setIsApprover(e.target.checked)} /> Approves other sub-accounts' carts</label>
      </div>
      <div>
        <div className="iw-lbl">Their orders are approved by</div>
        <div className="mr-chips">
          {others.map((x) => (
            <label key={x.id} className="chk mr-check">
              <input type="checkbox" checked={approvers.includes(x.id)}
                onChange={(e) => setApprovers(e.target.checked ? [...approvers, x.id] : approvers.filter((y) => y !== x.id))} />
              {x.name ?? x.email ?? `#${x.id}`}
            </label>
          ))}
          {others.length === 0 && <span className="muted">No one else at this camp.</span>}
        </div>
      </div>
      <div>
        <div className="iw-lbl">Their own blocked categories</div>
        <CategoryPicker categories={cats} ids={blocks} onChange={setBlocks} />
      </div>
      <div className="edi-actions">
        <OutcomeLine o={outcome} />
        <div className="actions">
          <button className="btn-sm ghost" disabled={busy} onClick={onCancel}>Close</button>
          <button className="btn-sm" disabled={Object.keys(body).length === 0 || busy} onClick={() => onSave(body)}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function VendorsCard({ d, canEdit, busy, outcome, onAssign, onSave }: {
  d: CampRecord; canEdit: boolean; busy: string; outcome: Outcome | null; onAssign: (vendorId: number) => void;
  onSave: (vendorId: number, body: Body) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const active = d.vendors.filter((v) => v.is_active).length;
  return (
    <section className="card">
      <h2>Assigned vendors ({active} active)</h2>
      <p className="muted mr-lead">Who this camp orders from, and each vendor's minimum order, delivery days, cut-off and lead time for it. Checkout applies these to the next order.</p>
      {editing == null && <p className="mr-lead"><OutcomeLine o={outcome} /></p>}
      {canEdit && <div className="mr-assign"><VendorPicker placeholder="Assign a vendor…" exclude={d.vendors.filter((v) => v.is_active).map((v) => v.vendor_id)} onPick={(vid) => onAssign(vid)} /></div>}
      <div className="mr-scroll">
        <table>
          <thead><tr><th>Vendor</th><th>Assignment</th><th>Account #</th><th className="num">Min order</th><th>Delivery days</th><th>Cut-off</th><th>Lead time</th><th></th></tr></thead>
          <tbody>
            {d.vendors.map((v) => (
              editing === v.vendor_id ? (
                <tr key={v.vendor_id}><td colSpan={8}>
                  <PairEditor name={v.vendor ?? `Vendor ${v.vendor_id}`} pair={v} busy={busy === `vendor-${v.vendor_id}`} outcome={outcome}
                    onCancel={() => setEditing(null)} onSave={async (body) => { if (await onSave(v.vendor_id, body)) setEditing(null); }} />
                </td></tr>
              ) : (
                <tr key={v.vendor_id} className={v.is_active ? undefined : 'inactive'}>
                  <td>{v.vendor} <span className="mono">#{v.vendor_id}</span></td>
                  <td>{v.is_active ? <span className="pill type">active</span> : <span className="muted">inactive</span>}</td>
                  <td className="mono">{v.account_number ?? '—'}</td>
                  <td className="num">{v.min_order_amount ? money(v.min_order_amount) : '—'}</td>
                  <td className="small">{dayNames(v.allowed_week_days)}</td>
                  <td>{v.cutoff_time ?? '—'}</td>
                  <td>{v.order_lead_time ?? '—'}</td>
                  <td>{canEdit && <button className="btn-sm ghost" disabled={busy !== '' || editing != null} onClick={() => setEditing(v.vendor_id)}>Edit</button>}</td>
                </tr>
              )
            ))}
            {d.vendors.length === 0 && <tr><td colSpan={8} className="loading">No vendor is assigned to this camp.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One camp↔vendor pair's delivery settings (shared with the vendor record). */
export function PairEditor({ name, pair, busy, outcome, onSave, onCancel }: {
  name: string; pair: Omit<CampVendor, 'vendor_id' | 'vendor'>; busy: boolean; outcome: Outcome | null;
  onSave: (body: Body) => void; onCancel: () => void;
}) {
  const init = {
    minOrderAmount: pair.min_order_amount ? String(pair.min_order_amount) : '',
    minOrderQty: pair.min_order_qty ? String(pair.min_order_qty) : '',
    cutoffTime: pair.cutoff_time ?? '', orderLeadTime: pair.order_lead_time ?? '',
  };
  const [f, setF] = useState(init);
  const [days, setDays] = useState(parseDays(pair.allowed_week_days));
  const [active, setActive] = useState(pair.is_active);
  const body: Body = {};
  for (const k of Object.keys(init) as Array<keyof typeof init>) if (f[k] !== init[k]) body[k] = f[k].trim() || null;
  if (JSON.stringify(days) !== JSON.stringify(parseDays(pair.allowed_week_days))) body.allowedWeekDays = days;
  if (active !== pair.is_active) body.isActive = active;
  return (
    <div className="stack mr-tight mr-edit">
      <strong>{name}</strong>
      <div className="tf-grid">
        <label>Minimum order ($)<input inputMode="decimal" value={f.minOrderAmount} placeholder="none" onChange={(e) => setF({ ...f, minOrderAmount: e.target.value })} /></label>
        <label>Minimum quantity<input inputMode="decimal" value={f.minOrderQty} placeholder="none" onChange={(e) => setF({ ...f, minOrderQty: e.target.value })} /></label>
        <label>Cut-off time<input value={f.cutoffTime} placeholder="e.g. 14:00" onChange={(e) => setF({ ...f, cutoffTime: e.target.value })} /></label>
        <label>Lead time (days)<input value={f.orderLeadTime} placeholder="—" onChange={(e) => setF({ ...f, orderLeadTime: e.target.value })} /></label>
        <label className="chk"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Assigned (active)</label>
      </div>
      <div><div className="iw-lbl">Delivery days</div><WeekdayPicker days={days} onChange={setDays} /></div>
      <div className="edi-actions">
        <OutcomeLine o={outcome} />
        <div className="actions">
          <button className="btn-sm ghost" disabled={busy} onClick={onCancel}>Close</button>
          <button className="btn-sm" disabled={Object.keys(body).length === 0 || busy} onClick={() => onSave(body)}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
